import React, { useEffect } from 'react'
import { Table } from 'antd'
import { GoldenFilled } from '@ant-design/icons'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import Loading from '../../components/styled/Loading'
import { antdTableHorizontalScroll } from 'src/components/styled/horizontalScroll'
import { useDispatch, useSelector } from 'react-redux'
import { fetchStockTakeProducts } from '../../features/StockSlice'
import { VAT_PERCENTAGE, VAT_RATE } from '../../config'
import ValuationCSV from './ValuationCSV'
import useStockRoleCapabilities from 'src/hooks/useStockRoleCapabilities'
import StockLimit from './StockLimit'
import { canUseStockTakeReports } from 'src/utils/stockTakePlanGate'
import {
  formatWastageReportDateHeading,
  wastageOpenCountLine,
  wastageReportDateRange,
} from 'src/constants/wastage'

const Valuation = props => {
  const {
    match: {
      params: { stockTakeId, venueId },
    },
  } = props
  const isLoading = useSelector(state => state.stockTakes.isLoading)
  const stockTakes = useSelector(state => state.stockTakes.stockTakes)
  const stockTake = stockTakes?.find(stockTake => stockTake._id === stockTakeId)
  const dispatch = useDispatch()
  const { canViewExpectedAndVariance } = useStockRoleCapabilities()
  const currentPlan = useSelector(
    state => state.venue?.venue?.usageLimits?.currentPlan,
  )
  const stockTakeProductsFromDb = useSelector(
    state => state.stockTakes.stockTakeProducts,
  )
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search || '')
  const endpoint =
    location.pathname.split('/')[location.pathname.split('/').length - 1]
  const isEndPointWastage = endpoint === 'wastage-report'
  const isEndPointStock = endpoint === 'valuation-report'
  const isPartialStockTake = (stockTake?.countType || 'MAIN') === 'PARTIAL'
  const explicitPartialView =
    queryParams.get('view') === 'partial' ||
    queryParams.get('countType') === 'PARTIAL'
  const blockOfficialViewForPartial = isPartialStockTake && !explicitPartialView

  const wastageDateRange = wastageReportDateRange(stockTake, stockTakes)
  const wastageDateHeading = formatWastageReportDateHeading(wastageDateRange)
  const stockTakeProducts = isEndPointWastage
    ? stockTakeProductsFromDb?.filter(product => product.wastage > 0)
    : stockTakeProductsFromDb?.filter(product => product.inStock > 0)
  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '15%',
      sorter: (a, b) =>
        String(a.title).trim().localeCompare(String(b.title).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) =>
        String(a.size).trim().localeCompare(String(b.size).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'UoM',
      dataIndex: 'UoM',
      key: 'UoM',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplier',
      width: '12%',
      sorter: (a, b) =>
        String(a.supplierTitle)
          .trim()
          .localeCompare(String(b.supplierTitle).trim()),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Sku',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: price => {
        return `£${price}`
      },
      sorter: (a, b) =>
        (a.hasVat ? VAT_RATE * a.price : a.price) -
        (b.hasVat ? VAT_RATE * b.price : b.price),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Prev qty',
      dataIndex: 'lastQuantity',
      key: 'lastQuantity',
      className: 'smallColumn',
    },
    {
      title: 'Received',
      dataIndex: 'qtySinceLastStockTake',
      key: 'qtySinceLastStockTake',
      className: 'smallColumn',
    },
    {
      title: 'In Stock',
      dataIndex: 'inStock',
      key: 'inStock',
      className: 'smallColumn',
      hidden: isEndPointStock,
      sorter: (a, b) => a.inStock - b.inStock,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Stock value',
      key: 'stockAmount',
      className: 'smallColumn',
      hidden: isEndPointStock,
      render: product => {
        return `£${(product.hasVat
          ? product.price * product.inStock * VAT_RATE
          : product.price * product.inStock
        ).toFixed(2)}`
      },
    },
    {
      title: 'Wastage',
      dataIndex: 'wastage',
      key: 'wastage',
      className: 'smallColumn',
      hidden: isEndPointWastage,
    },
    {
      title: 'Wastage value',
      key: 'wastageAmount',
      className: 'smallColumn',
      hidden: isEndPointWastage,
      render: product => {
        return `£${(product.hasVat
          ? product.price * product.wastage * VAT_RATE
          : product.price * product.wastage
        ).toFixed(2)}`
      },
    },
    {
      title: 'Weighted',
      key: 'weighted',
      className: 'weightedColumn smallColumn',
      render: product => {
        const includesInUoM = product.UoM
          ? product.UoM.toLowerCase() === 'kg' ||
            product.UoM.toLowerCase() === 'g'
          : false
        return includesInUoM ? <GoldenFilled /> : ''
      },
    },
    {
      title: 'Net',
      key: 'net',
      render: product => {
        return `£${(product.price * product.inStock).toFixed(2)}`
      },
      sorter: (a, b) => a.price * a.inStock - b.price * b.inStock,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'VAT',
      key: 'vat',
      render: product => {
        const vat = product.hasVat ? product.price * VAT_PERCENTAGE : 0
        return `£${(vat * product.inStock).toFixed(2)}`
      },
      sorter: (a, b) =>
        (a.hasVat ? a.price * VAT_PERCENTAGE * a.inStock : 0) - b.hasVat
          ? b.price * VAT_PERCENTAGE * b.inStock
          : 0,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Net',
      key: 'net',
      render: product => {
        return `£${(product.price * product.inStock).toFixed(2)}`
      },
      sorter: (a, b) => a.price * a.inStock - b.price * b.inStock,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Gross',
      key: 'gross',
      render: product => {
        const priceWithVat = product.hasVat
          ? product.price * VAT_RATE
          : product.price
        return `£${(priceWithVat * product.inStock).toFixed(2)}`
      },
      sorter: (a, b) =>
        (a.hasVat ? VAT_RATE * a.price * a.inStock : a.price * a.inStock) -
        (b.hasVat ? VAT_RATE * b.price * b.inStock : b.price * b.inStock),
      sortDirections: ['ascend', 'descend'],
    },
  ]
    .filter(item => item.hidden === undefined || item.hidden === true)
    .filter(
      item =>
        canViewExpectedAndVariance ||
        (item.key !== 'lastQuantity' && item.key !== 'qtySinceLastStockTake'),
    )
  const totals = {
    net: stockTakeProducts
      .reduce((sum, item) => {
        const currentQuantity = isEndPointWastage ? item.wastage : item.inStock
        return sum + item.price * currentQuantity
      }, 0)
      .toFixed(2),
    VAT: stockTakeProducts
      .reduce((sum, item) => {
        const currentQuantity = isEndPointWastage ? item.wastage : item.inStock
        return (
          sum +
          (item.hasVat ? item.price * currentQuantity * VAT_PERCENTAGE : 0)
        )
      }, 0)
      .toFixed(2),
    gross: stockTakeProducts
      .reduce((sum, item) => {
        const currentQuantity = isEndPointWastage ? item.wastage : item.inStock
        return (
          sum +
          (item.hasVat
            ? item.price * currentQuantity * VAT_RATE
            : item.price * currentQuantity)
        )
      }, 0)
      .toFixed(2),
  }

  useEffect(() => {
    dispatch(fetchStockTakeProducts(venueId, stockTakeId))
  }, [venueId, dispatch])

  if (!canUseStockTakeReports(currentPlan)) {
    return <StockLimit />
  }

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div>
          <BackLink to={`/v/${venueId}/stockTakes`}>Go back</BackLink>
          <StyledDiv>
            <h1>
              {`Stock ${isEndPointStock ? 'valuation' : 'wastage'}`}
              {wastageDateHeading ? (
                <StyledSpan>{wastageDateHeading}</StyledSpan>
              ) : null}
            </h1>
            {!blockOfficialViewForPartial && (
              <ValuationCSV
                stockTakeProducts={stockTakeProducts}
                stockTake={stockTake}
                dateRange={wastageDateRange}
                totals={totals}
                endpoint={endpoint}
              />
            )}
          </StyledDiv>
          {blockOfficialViewForPartial ? (
            <PartialGuardNotice>
              Main Stock Take is the official reporting source of truth. This is
              a Partial Count and is excluded from official valuation/wastage
              reports unless explicitly viewed as partial.
            </PartialGuardNotice>
          ) : (
            <ValuationTableWrap>
              <Table
                pagination={false}
                bordered
                dataSource={stockTakeProducts}
                locale={{
                  emptyText:
                    isEndPointWastage && !stockTake?.isLocked
                      ? wastageOpenCountLine(stockTake, wastageDateRange.to)
                      : undefined,
                }}
                scroll={{ x: 1800, y: '55vh' }}
                columns={columns}
                footer={() => {
                  const kind = isEndPointWastage ? 'Wastage' : 'Valuation'
                  return (
                    <TotalsFooter>
                      <TotalsLine>{`${kind} Net total: £${totals.net}`}</TotalsLine>
                      <TotalsLine>{`${kind} Total VAT: £${totals.VAT}`}</TotalsLine>
                      <TotalsLine>{`${kind} Gross total: £${totals.gross}`}</TotalsLine>
                    </TotalsFooter>
                  )
                }}
              />
            </ValuationTableWrap>
          )}
        </div>
      )}
    </>
  )
}

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.85rem;
  border: 1.5px solid ${({ theme }) => theme.colours?.panSilver || '#e0dfdf'};
  border-radius: 8px;
  background: ${({ theme }) => theme.colours?.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours?.seaweedGreen || '#132d07'};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.2;
  text-decoration: none;
`

const ValuationTableWrap = styled.div`
  width: 100%;
  min-width: 0;
  ${antdTableHorizontalScroll}
`

const StyledDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSpan = styled.span`
  margin-left: 5px;
  font-size: 20px;
`

const TotalsFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`

const TotalsLine = styled.div`
  font-weight: 700;
  white-space: nowrap;
`

const PartialGuardNotice = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ffd591;
  background: #fff7e6;
  color: #874d00;
  font-size: 13px;
`

export default Valuation
